import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import callApi from "Common/utils/apiCaller";
import { showNotification } from "Common/utils/Notification";
import { formatCountdown, getSyncRetrySeconds } from "Common/utils/syncLimit";
import LoaderComponent from "Common/Components/Loader/Loader";
import { emitSourceSaved, SOURCE_CHOOSE, SOURCE_SAVED, VERIFY_SOURCE } from "./sourceEvents";

// "Verify source files": a card on the index page, in the spirit of opening the source
// server in FileZilla. Connect with a login, then look at what the server generated:
// today's files, a time range, or the expected file for each index day. Also holds the
// re-pull of values from the source. This is also where a Solactive login is typed and
// saved, once. The rules live in the API.

type Ticker = { returnType: string; indexCode: string };
type Props = { tickers: Ticker[] };
type View = "today" | "range" | "days";

const SOLACTIVE = "solective";
const INPUT_TIME = "YYYY-MM-DDTHH:mm";

const formatSize = (bytes: number | null) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const formatWhen = (iso: string | null) => (iso ? moment(iso).format("DD MMM YYYY, HH:mm") : "");
const compareValues = (stored: number | null, file: number | null) => {
  if (stored === null || file === null) return "";
  return Math.abs(stored - file) < 0.00005 ? "Match" : "Different";
};

const TONES: Record<string, string> = {
  success: "border-green-500 bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300",
  warning: "border-yellow-500 bg-yellow-50 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300",
  error: "border-red-500 bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-300",
  info: "border-sky-500 bg-sky-50 text-sky-800 dark:bg-sky-500/10 dark:text-sky-300",
};

const Banner: React.FC<{ tone: string; title: string; children?: React.ReactNode; action?: React.ReactNode }> = ({
  tone,
  title,
  children,
  action,
}) => (
  <div role="status" className={`flex flex-wrap items-center gap-3 p-3 border-l-4 rounded-md ${TONES[tone]}`}>
    <div className="grow">
      <p className="font-medium">{title}</p>
      {children && <div className="mt-0.5 text-sm opacity-90">{children}</div>}
    </div>
    {action}
  </div>
);

const labelCls = "block mb-1 text-xs font-medium text-slate-500 dark:text-zink-200";
const primaryBtn =
  "text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600";
const th = "px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500";
const td = "px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500";

const SourceVerification: React.FC<Props> = ({ tickers }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState<string>(tickers[0]?.indexCode || "");
  const [config, setConfig] = useState<any>(null);
  const [configError, setConfigError] = useState("");
  const [tab, setTab] = useState<"verify" | "repull">("verify");
  const [highlight, setHighlight] = useState(false);
  const [pendingCode, setPendingCode] = useState(""); // a ticker where Solactive was picked but has no login yet
  const pendingRef = useRef("");
  const [savingLogin, setSavingLogin] = useState(false);

  // connect
  const [creds, setCreds] = useState({ host: "", port: "22", username: "", password: "" });
  const [useSaved, setUseSaved] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connection, setConnection] = useState<any>(null);
  const [connectError, setConnectError] = useState("");

  // look
  const [view, setView] = useState<View>("today");
  const [rangeFrom, setRangeFrom] = useState(moment().startOf("day").format(INPUT_TIME));
  const [rangeTo, setRangeTo] = useState(moment().format(INPUT_TIME));
  const [daysFrom, setDaysFrom] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [daysTo, setDaysTo] = useState(moment().format("YYYY-MM-DD"));
  const [includeValues, setIncludeValues] = useState(false);
  const [querying, setQuerying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [checkCooldownUntil, setCheckCooldownUntil] = useState(0);

  // re-pull
  const [syncStart, setSyncStart] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [syncEnd, setSyncEnd] = useState(moment().format("YYYY-MM-DD"));
  const [syncing, setSyncing] = useState(false);
  const [queued, setQueued] = useState<string[]>([]);
  const [syncCooldownUntil, setSyncCooldownUntil] = useState(0);
  const [clock, setClock] = useState(Date.now());

  const today = moment().format("YYYY-MM-DD");
  const tickerKey = tickers.map((t) => t.indexCode).join(",");
  const activeTicker = tickers.find((t) => t.indexCode === active);
  const sources: { value: string; label: string }[] = config?.sources || [];
  const sourceLabel = (value: string) => sources.find((s) => s.value === value)?.label || value;
  const pendingSolactive = pendingCode !== "" && pendingCode === active;
  const savedSolactive = config?.data_source === SOLACTIVE;
  const isSolactive = savedSolactive || pendingSolactive;
  const checkWait = Math.max(0, Math.ceil((checkCooldownUntil - clock) / 1000));
  const syncWait = Math.max(0, Math.ceil((syncCooldownUntil - clock) / 1000));

  const resetCheck = () => {
    setConnection(null);
    setConnectError("");
    setResult(null);
  };

  const loadConfig = useCallback(async (code: string) => {
    if (!code) return;
    setConfigError("");
    try {
      const res: any = await callApi(`v1/index-datasource/by-index/${encodeURIComponent(code)}`, "get");
      const next = res?.data?.config;
      setConfig(next);
      setCreds({
        host: next?.credential?.host || (pendingRef.current === code ? "clients.solactive.com" : ""),
        port: String(next?.credential?.port || (pendingRef.current === code ? "10022" : "22")),
        username: "",
        password: "",
      });
      setUseSaved(false);
    } catch (error: any) {
      setConfig(null);
      setConfigError(
        error?.status === 403 ? "You do not have access to data source settings." : error?.message || "Could not load the data source."
      );
    }
  }, []);

  // Pick a ticker (or the first one) and load its settings.
  useEffect(() => {
    if (!tickers.length) return;
    const code = tickers.some((t) => t.indexCode === active) ? active : tickers[0].indexCode;
    if (code !== active) setActive(code);
    setConfig(null);
    resetCheck();
    setQueued([]);
    loadConfig(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tickerKey, loadConfig]);

  // The slim Data source line under a ticker talks to this card through two events.
  useEffect(() => {
    const onVerify = (e: any) => {
      const code = e?.detail?.indexCode;
      if (!tickers.some((t) => t.indexCode === code)) return;
      setActive(code);
      setTab("verify");
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2500);
      setTimeout(() => passwordRef.current?.focus({ preventScroll: true }), 900);
    };
    const onChoose = (e: any) => {
      const code = e?.detail?.indexCode;
      if (!tickers.some((t) => t.indexCode === code)) return;
      pendingRef.current = code;
      setPendingCode(code);
      if (code === active) {
        setCreds((c) => ({ ...c, host: c.host || "clients.solactive.com", port: c.port === "22" ? "10022" : c.port }));
      }
      setActive(code);
      setTab("verify");
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2500);
    };
    const onSaved = (e: any) => {
      if (e?.detail?.indexCode === active) {
        pendingRef.current = "";
        setPendingCode("");
        resetCheck();
        loadConfig(active);
      }
    };
    window.addEventListener(VERIFY_SOURCE, onVerify);
    window.addEventListener(SOURCE_SAVED, onSaved);
    window.addEventListener(SOURCE_CHOOSE, onChoose);
    return () => {
      window.removeEventListener(SOURCE_CHOOSE, onChoose);
      window.removeEventListener(VERIFY_SOURCE, onVerify);
      window.removeEventListener(SOURCE_SAVED, onSaved);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerKey, active, loadConfig]);

  useEffect(() => {
    if (Math.max(checkCooldownUntil, syncCooldownUntil) <= Date.now()) return;
    const id = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(id);
  }, [checkCooldownUntil, syncCooldownUntil]);

  const api = (path: string) => `v1/index-datasource/by-index/${encodeURIComponent(active)}/${path}`;

  // What is on the screen. A password is only sent when typed; otherwise the saved one is used.
  const credentialPayload = () => {
    const username = creds.username || (useSaved ? config?.credential?.username : "") || "";
    const c: any = { host: creds.host, port: creds.port, username };
    if (!useSaved) c.password = creds.password;
    return c;
  };

  const onCheckError = (error: any, fallback: string) => {
    const wait = getSyncRetrySeconds(error);
    if (wait) {
      setCheckCooldownUntil(Date.now() + wait * 1000);
      setClock(Date.now());
    }
    showNotification(error?.message || fallback, wait ? "warning" : "error");
  };

  const runQuery = async (mode: View) => {
    setQuerying(true);
    try {
      let res: any;
      if (mode === "days") {
        res = await callApi(
          api(`source/files?start_date=${daysFrom}&end_date=${daysTo}&include_values=${includeValues}`),
          "post",
          { credential: credentialPayload() }
        );
      } else {
        const from = mode === "today" ? moment().startOf("day") : moment(rangeFrom);
        const to = mode === "today" ? moment() : moment(rangeTo);
        if (!from.isValid() || !to.isValid()) {
          showNotification("Pick a start and an end time", "error");
          return;
        }
        res = await callApi(
          api(`source/recent?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
          "post",
          { credential: credentialPayload() }
        );
      }
      setResult({ mode, data: res?.data?.result, ranAt: new Date(), valuesRead: mode === "days" && includeValues });
    } catch (error: any) {
      onCheckError(error, "Could not check the files");
    } finally {
      setQuerying(false);
    }
  };

  const handleConnect = async () => {
    if (!useSaved && !creds.password) {
      showNotification("Type the password, or tick Use the saved password", "error");
      return;
    }
    setConnecting(true);
    resetCheck();
    try {
      const res: any = await callApi(api("source/test"), "post", { credential: credentialPayload() });
      const r = res?.data?.result;
      if (r?.connected) {
        setConnection({ host: r.host, port: r.port, total: r.total, directory: r.directory });
        setView("today");
        // Show something useful straight away.
        await runQuery("today");
      } else {
        setConnectError(`${r?.error?.message || "Could not connect."} (${r?.host}:${r?.port})`);
      }
    } catch (error: any) {
      onCheckError(error, "Could not connect");
    } finally {
      setConnecting(false);
    }
  };

  // Saves what is typed as this ticker's Solactive login and makes Solactive its source.
  const handleSaveLogin = async () => {
    if (!creds.host || !creds.username || !creds.password) {
      showNotification("Type the host, username and password first", "error");
      return;
    }
    setSavingLogin(true);
    try {
      const body = {
        data_source: SOLACTIVE,
        credential: { host: creds.host, port: creds.port, username: creds.username, password: creds.password },
      };
      await callApi(`v1/index-datasource/by-index/${encodeURIComponent(active)}`, "post", body);
      showNotification("Solactive login saved", "success");
      pendingRef.current = "";
      setPendingCode("");
      setCreds((c) => ({ ...c, password: "" }));
      emitSourceSaved(active, SOLACTIVE);
    } catch (error: any) {
      showNotification(error?.message || "Could not save the login", "error");
    } finally {
      setSavingLogin(false);
    }
  };

  const handleSync = async () => {
    if (!syncStart || !syncEnd) {
      showNotification("Pick a start and an end date", "error");
      return;
    }
    const ok = window.confirm(
      `Re-pull ${active} from ${sourceLabel(config?.data_source)} for ${syncStart} to ${syncEnd}?\n\n` +
        "Weekdays only. The stored values and holdings for those days will be replaced with what the source returns."
    );
    if (!ok) return;
    setSyncing(true);
    setQueued([]);
    try {
      const res: any = await callApi(api(`sync?start_date=${syncStart}&end_date=${syncEnd}`), "post", {});
      setQueued(res?.data?.dates || []);
      showNotification(res?.data?.message || "Sync queued", "success");
    } catch (error: any) {
      const wait = getSyncRetrySeconds(error);
      if (wait) {
        setSyncCooldownUntil(Date.now() + wait * 1000);
        setClock(Date.now());
        showNotification(error?.message || "Too many syncs for this index. Try again later.", "warning");
      } else {
        showNotification(error?.message || "Could not start the sync", "error");
      }
    } finally {
      setSyncing(false);
    }
  };

  // ---- results -----------------------------------------------------------------------

  const checkAgain = (
    <button type="button" disabled={querying || checkWait > 0} className="font-medium underline" onClick={() => runQuery(result.mode)}>
      {checkWait > 0 ? `Wait ${formatCountdown(checkWait)}` : "Check again"}
    </button>
  );

  const renderResult = () => {
    if (!result) return null;
    const d = result.data;
    if (!d) return null;
    if (!d.connected) {
      return <Banner tone="error" title="Could not reach the server">{`${d.error?.message || ""} (${d.host}:${d.port})`}</Banner>;
    }

    if (result.mode === "days") {
      const missing = d.rows.filter((r: any) => !r.found).map((r: any) => r.date);
      const banner =
        d.found === 0 ? (
          <Banner tone="warning" title="No file generated yet for these days" action={checkAgain}>
            The server does not have the expected file for any of the {d.total} day(s).
          </Banner>
        ) : missing.length ? (
          <Banner tone="warning" title={`${d.found} of ${d.total} days have a file`} action={checkAgain}>
            {`Not there yet: ${missing.join(", ")}`}
          </Banner>
        ) : (
          <Banner tone="success" title={`All ${d.total} day(s) have a file`} />
        );
      return (
        <div className="space-y-3">
          {banner}
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                <tr>
                  <th className={th}>Date</th>
                  <th className={th}>File</th>
                  <th className={th}>On server</th>
                  <th className={th}>Generated</th>
                  <th className={th}>Size</th>
                  <th className={th}>Stored value</th>
                  {result.valuesRead && <th className={th}>File value</th>}
                  {result.valuesRead && <th className={th}>Check</th>}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r: any) => (
                  <tr key={r.date}>
                    <td className={td}>{r.date}</td>
                    <td className={`${td} break-all whitespace-normal`}>{r.file_name}</td>
                    <td className={`${td} ${r.found ? "text-green-600" : "text-red-500"}`}>{r.found ? "Found" : "Missing"}</td>
                    <td className={td}>{formatWhen(r.modified)}</td>
                    <td className={td}>{formatSize(r.size)}</td>
                    <td className={td}>{r.stored_value === null ? "none stored" : r.stored_value}</td>
                    {result.valuesRead && <td className={td}>{r.file_value === null ? "" : r.file_value}</td>}
                    {result.valuesRead && (
                      <td className={td}>
                        {r.file_error ? (
                          <span className="text-slate-500">{r.file_error}</span>
                        ) : (
                          <span className={compareValues(r.stored_value, r.file_value) === "Different" ? "text-red-500" : "text-green-600"}>
                            {compareValues(r.stored_value, r.file_value)}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // today / time range
    const period = result.mode === "today" ? "today" : "in this time range";
    if (d.matched === 0) {
      return (
        <Banner
          tone="warning"
          title={result.mode === "today" ? "No file generated yet today" : "No file was generated in this time range"}
          action={checkAgain}
        >
          {`The folder has ${d.total_in_folder} item(s), none modified ${period}. The source usually publishes after market close.`}
        </Banner>
      );
    }
    const latest = d.entries[0];
    return (
      <div className="space-y-3">
        <Banner tone="success" title={`${d.matched} file(s) generated ${period}`}>
          {`Latest: ${latest.name}, ${formatWhen(latest.modified)}, ${formatSize(latest.size)}`}
        </Banner>
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
              <tr>
                <th className={th}>File</th>
                <th className={th}>Generated</th>
                <th className={th}>Size</th>
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e: any) => (
                <tr key={e.name}>
                  <td className={td}>{e.name}</td>
                  <td className={td}>{formatWhen(e.modified)}</td>
                  <td className={td}>{formatSize(e.size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ---- layout ------------------------------------------------------------------------

  if (!tickers.length) return null;

  const tabBtn = (id: "verify" | "repull", text: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 -mb-px border-b-2 ${
        tab === id ? "border-custom-500 text-custom-500 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {text}
    </button>
  );

  const viewBtn = (id: View, text: string) => (
    <button
      type="button"
      onClick={() => setView(id)}
      className={`px-3 py-1.5 text-sm rounded-md border ${
        view === id
          ? "bg-custom-500 border-custom-500 text-white"
          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-transparent dark:border-zink-500 dark:text-zink-100"
      }`}
    >
      {text}
    </button>
  );

  return (
    <div className="grid grid-cols-12 gap-x-5" ref={rootRef}>
      <div className={`col-span-12 card 2xl:col-span-12 transition-shadow duration-500 ${highlight ? "ring-2 ring-custom-500" : ""}`}>
        <div className="card-body">
          <div className="flex items-center">
            <h6 className="text-15 grow">Verify source files</h6>
          </div>
          <p className="mt-1 mb-4 text-slate-500">Check that the source server has generated your index files.</p>

          {tickers.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Ticker">
              {tickers.map((t) => (
                <button
                  key={t.indexCode}
                  type="button"
                  role="tab"
                  aria-selected={t.indexCode === active}
                  onClick={() => setActive(t.indexCode)}
                  className={`px-3 py-1.5 text-sm rounded-full border ${
                    t.indexCode === active
                      ? "bg-custom-500 border-custom-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-transparent dark:border-zink-500 dark:text-zink-100"
                  }`}
                >
                  {`${t.returnType} · ${t.indexCode}`}
                </button>
              ))}
            </div>
          )}

          {configError && <Banner tone="error" title="Could not load this ticker's data source">{configError}</Banner>}
          {!config && !configError && <LoaderComponent />}

          {config && !isSolactive && (
            <Banner tone="info" title={`${activeTicker?.indexCode} uses ${config.has_record ? sourceLabel(config.data_source) : "ICAL3 (default)"}`}>
              Source files only exist for Solactive. Choose Solactive in this ticker's Data source field above, and this card unlocks.
            </Banner>
          )}

          {config && isSolactive && (
            <>
              {pendingSolactive && !savedSolactive ? (
                <p className="mb-3 text-sm text-slate-500">
                  Solactive is chosen for {activeTicker?.indexCode}. Type its SFTP login below, press Connect to test it, then Save login to switch.
                </p>
              ) : (
                <div className="flex border-b border-slate-200 dark:border-zink-500">
                  {tabBtn("verify", "Verify files")}
                  {tabBtn("repull", "Re-pull values")}
                </div>
              )}

              {(tab === "verify" || (pendingSolactive && !savedSolactive)) && (
                <div className="mt-4 space-y-5">
                  {/* Step 1: connect */}
                  {!connection ? (
                    <div>
                      <p className="mb-3 font-medium">1. Connect to the server</p>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                        <div className="md:col-span-2">
                          <label className={labelCls}>Host</label>
                          <input className="w-full form-input" value={creds.host} onChange={(e) => setCreds({ ...creds, host: e.target.value })} placeholder="clients.solactive.com" />
                        </div>
                        <div>
                          <label className={labelCls}>Port</label>
                          <input className="w-full form-input" value={creds.port} onChange={(e) => setCreds({ ...creds, port: e.target.value })} inputMode="numeric" />
                        </div>
                        <div>
                          <label className={labelCls}>Username</label>
                          <input className="w-full form-input" name="source-check-user" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} autoComplete="off" placeholder="Type the username" />
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Password</label>
                          <input
                            ref={passwordRef}
                            className="w-full form-input"
                            type="password"
                            value={creds.password}
                            disabled={useSaved}
                            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                            autoComplete="new-password"
                            placeholder={useSaved ? "Using the saved password" : "Type the password"}
                            onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                          />
                        </div>
                        {config.credential?.has_password && (
                          <label className="flex items-center gap-2 mt-6 md:col-span-2">
                            <input type="checkbox" checked={useSaved} onChange={(e) => setUseSaved(e.target.checked)} />
                            <span>Use the saved password</span>
                          </label>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-slate-500">Used for this check only, unless you press Save login.</p>
                      <div className="mt-3">
                        <button type="button" disabled={connecting || checkWait > 0} onClick={handleConnect} className={primaryBtn}>
                          {connecting ? <LoaderComponent /> : checkWait > 0 ? `Wait ${formatCountdown(checkWait)}` : "Connect"}
                        </button>
                        {!useSaved && creds.username && creds.password && (
                          <button
                            type="button"
                            disabled={savingLogin}
                            onClick={handleSaveLogin}
                            className="ml-3 btn border-custom-500 text-custom-500 hover:bg-custom-50"
                          >
                            {savingLogin ? <LoaderComponent /> : pendingSolactive && !savedSolactive ? "Save login and use Solactive" : "Save login"}
                          </button>
                        )}
                      </div>
                      {connectError && (
                        <div className="mt-3">
                          <Banner tone="error" title="Could not connect">{connectError}</Banner>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="px-2 py-0.5 text-green-700 bg-green-100 rounded-full dark:bg-green-500/20 dark:text-green-300">✓ Connected</span>
                      <span className="text-slate-500">{`${connection.host}:${connection.port} · ${connection.total} item(s) in "${connection.directory}"`}</span>
                      <button type="button" className="ml-auto text-custom-500 hover:underline" onClick={resetCheck}>
                        Change login
                      </button>
                    </div>
                  )}

                  {/* Step 2: what to look at */}
                  {connection && (
                    <div>
                      <p className="mb-3 font-medium">2. What do you want to see?</p>
                      <div className="flex flex-wrap gap-2">
                        {viewBtn("today", "Today's files")}
                        {viewBtn("range", "A time range")}
                        {viewBtn("days", "Expected files for index days")}
                      </div>

                      <div className="grid items-end grid-cols-1 gap-3 mt-3 md:grid-cols-4">
                        {view === "range" && (
                          <>
                            <div>
                              <label className={labelCls}>From</label>
                              <input type="datetime-local" className="w-full form-input" value={rangeFrom} max={moment().format(INPUT_TIME)} onChange={(e) => setRangeFrom(e.target.value)} />
                            </div>
                            <div>
                              <label className={labelCls}>To</label>
                              <input type="datetime-local" className="w-full form-input" value={rangeTo} max={moment().format(INPUT_TIME)} onChange={(e) => setRangeTo(e.target.value)} />
                            </div>
                          </>
                        )}
                        {view === "days" && (
                          <>
                            <div>
                              <label className={labelCls}>From day</label>
                              <input type="date" className="w-full form-input" value={daysFrom} max={today} onChange={(e) => setDaysFrom(e.target.value)} />
                            </div>
                            <div>
                              <label className={labelCls}>To day</label>
                              <input type="date" className="w-full form-input" value={daysTo} max={today} onChange={(e) => setDaysTo(e.target.value)} />
                            </div>
                          </>
                        )}
                        <div>
                          <button type="button" disabled={querying || checkWait > 0} onClick={() => runQuery(view)} className={`${primaryBtn} w-full`}>
                            {querying ? <LoaderComponent /> : checkWait > 0 ? `Wait ${formatCountdown(checkWait)}` : "Show files"}
                          </button>
                        </div>
                      </div>
                      {view === "days" && (
                        <label className="flex items-center gap-2 mt-3 text-sm">
                          <input type="checkbox" checked={includeValues} onChange={(e) => setIncludeValues(e.target.checked)} />
                          <span>Also read the value inside each file and compare it with what is stored (first 5 files)</span>
                        </label>
                      )}
                      {view === "today" && <p className="mt-2 text-xs text-slate-500">Files generated since midnight on your clock.</p>}
                      {view === "days" && <p className="mt-1 text-xs text-slate-500">Weekdays only, up to 31 days.</p>}
                    </div>
                  )}

                  {connection && <div aria-live="polite">{querying && !result ? <LoaderComponent /> : renderResult()}</div>}
                </div>
              )}

              {tab === "repull" && (
                <div className="mt-4">
                  <p className="mb-1 font-medium">{`Re-pull values from ${sourceLabel(config.data_source)}`}</p>
                  <p className="mb-3 text-sm text-slate-500">
                    Fetches the closing values and holdings again for each weekday in the range and replaces what is stored. Check the files first.
                  </p>
                  <div className="grid items-end grid-cols-1 gap-3 md:grid-cols-4">
                    <div>
                      <label className={labelCls}>From day</label>
                      <input type="date" className="w-full form-input" value={syncStart} max={today} onChange={(e) => setSyncStart(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>To day</label>
                      <input type="date" className="w-full form-input" value={syncEnd} max={today} onChange={(e) => setSyncEnd(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <button type="button" disabled={syncing || syncWait > 0 || config.unrecognised} onClick={handleSync} className={`${primaryBtn} w-full`}>
                        {syncing ? <LoaderComponent /> : syncWait > 0 ? `Wait ${formatCountdown(syncWait)}` : `Sync from ${sourceLabel(config.data_source)}`}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Weekdays only, up to 31 days at a time.</p>
                  {queued.length > 0 && (
                    <div className="mt-3">
                      <Banner tone="success" title={`Queued ${queued.length} day(s)`}>
                        {`${queued.join(", ")}. Values appear as the background job finishes.`}
                      </Banner>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceVerification;
